import { NotificationSubscription } from '@models';
import { DocumentService, Inventory, InventoryService, SupplierLookup } from '@services';
import { message, Modal } from 'antd';
import { observer } from 'mobx-react';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import Presentation from './InventoryUploadDrawer.presentation';

declare const abp: any;

export interface InventoryUploadDrawerServiceOption {
  id: number;
  name: string;
}

export interface InventoryUploadDrawerHandle {
  open: (prefill?: {
    items?: any[];
    supplierId?: string;
    orderId?: string;
    serviceOptions?: InventoryUploadDrawerServiceOption[];
  }) => void;
}

interface IProps {
  categoryOptions: { label: string; value: string }[];
  onSuccess: (createdItems: Inventory[], parsedItems?: any[]) => void;
}

export const InventoryUploadDrawer = observer(
  forwardRef<InventoryUploadDrawerHandle, IProps>(({ categoryOptions, onSuccess }, ref) => {
    const [open, setOpen] = useState(false);
    const [uploadedFile, setUploadedFile] = useState<{
      base64: string;
      name: string;
      contentType: string;
    } | null>(null);
    const [documentId, setDocumentId] = useState<string | null>(null);
    const [parsedItems, setParsedItems] = useState<any[]>([]);
    const [parsedSupplierName, setParsedSupplierName] = useState('');
    const [parsedSupplierEmail, setParsedSupplierEmail] = useState('');
    const [parsedSupplierPhone, setParsedSupplierPhone] = useState('');
    const [parsedSupplierAddress, setParsedSupplierAddress] = useState('');
    const [parsedInvoiceNumber, setParsedInvoiceNumber] = useState('');
    const [bulkPurchaseDate, setBulkPurchaseDate] = useState<string | undefined>();
    const [bulkSupplierId, setBulkSupplierId] = useState<string | undefined>();
    const [bulkOrderId, setBulkOrderId] = useState<string | undefined>();
    const [serviceOptions, setServiceOptions] = useState<InventoryUploadDrawerServiceOption[]>([]);
    const [attachedSupplier, setAttachedSupplier] = useState<SupplierLookup | undefined>();
    const [supplierModalOpen, setSupplierModalOpen] = useState(false);
    const [supplierCreating, setSupplierCreating] = useState(false);
    const [parsing, setParsing] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [captureToken, setCaptureToken] = useState<string | null>(null);
    const captureTokenRef = useRef<string | null>(null);
    captureTokenRef.current = captureToken;

    useEffect(() => {
      const handler = async (userNotification: any) => {
        const name = userNotification?.notification?.notificationName;
        if (name !== NotificationSubscription.InventoryCaptureCompleted) return;
        const data = userNotification?.notification?.data?.properties;
        if (!data || data.token !== captureTokenRef.current) return;
        setDocumentId(data.documentId);
        try {
          const file = await DocumentService.get({ id: data.documentId });
          const contentType = file.contentType || 'image/jpeg';
          setUploadedFile({
            base64: `data:${contentType};base64,${file.base64Content}`,
            name: file.name || 'Mobile capture',
            contentType,
          });
          message.success('Invoice image received from mobile!');
        } catch (err) {
          console.error('Failed to load captured invoice', err);
          setUploadedFile({ base64: '', name: 'Mobile capture', contentType: 'image/jpeg' });
          message.warning('Invoice received but preview could not be loaded.');
        }
      };
      abp.event.on('abp.notifications.received', handler);
      return () => abp.event.off('abp.notifications.received', handler);
    }, []);

    useImperativeHandle(ref, () => ({
      open: async (prefill) => {
        setUploadedFile(null);
        setDocumentId(null);
        setCaptureToken(null);
        const options = prefill?.serviceOptions ?? [];
        setServiceOptions(options);
        setParsedItems(prefill?.items ?? []);
        setBulkSupplierId(prefill?.supplierId);
        setBulkOrderId(prefill?.orderId);
        setAttachedSupplier(undefined);
        setParsedSupplierName('');
        setParsedSupplierEmail('');
        setParsedSupplierPhone('');
        setParsedSupplierAddress('');
        setParsedInvoiceNumber('');
        setBulkPurchaseDate(undefined);
        setOpen(true);
        try {
          const session = await InventoryService.createCaptureSession();
          setCaptureToken(session.token);
        } catch {
          console.error('Failed to create capture session');
        }
      },
    }));

    const reset = () => {
      setOpen(false);
      setUploadedFile(null);
      setDocumentId(null);
      setParsedItems([]);
      setParsedSupplierName('');
      setParsedSupplierEmail('');
      setParsedSupplierPhone('');
      setParsedSupplierAddress('');
      setParsedInvoiceNumber('');
      setBulkPurchaseDate(undefined);
      setBulkSupplierId(undefined);
      setBulkOrderId(undefined);
      setServiceOptions([]);
      setAttachedSupplier(undefined);
      setCaptureToken(null);
    };

    const handleClose = () => {
      const hasUnsaved = !!uploadedFile || parsedItems.length > 0;
      if (hasUnsaved) {
        Modal.confirm({
          title: 'Discard changes?',
          content: 'You have an uploaded invoice or extracted items. Close without saving?',
          okText: 'Discard & close',
          cancelText: 'Keep editing',
          okButtonProps: { danger: true },
          onOk: reset,
        });
      } else {
        reset();
      }
    };

    const handleFileSelect = (file: File) => {
      const reader = new FileReader();
      reader.onload = () => {
        setUploadedFile({
          base64: reader.result as string,
          name: file.name,
          contentType: file.type,
        });
        setDocumentId(null);
      };
      reader.readAsDataURL(file);
    };

    const ensureUploaded = async (): Promise<string | null> => {
      if (documentId) return documentId;
      if (!uploadedFile || !uploadedFile.base64) return null;
      const doc = await InventoryService.uploadInvoice({
        name: uploadedFile.name,
        content: uploadedFile.base64,
        contentType: uploadedFile.contentType,
      });
      setDocumentId(doc.id);
      return doc.id;
    };

    const handleParse = async () => {
      if (!uploadedFile) return;
      setParsing(true);
      try {
        const parsed = await InventoryService.parseInvoice({
          name: uploadedFile.name,
          content: uploadedFile.base64,
          contentType: uploadedFile.contentType,
          categoryNames: categoryOptions.map((c) => c.label),
        });
        const categoryIdByName = new Map(
          categoryOptions.map((c) => [c.label.toLowerCase(), c.value])
        );
        const items = (parsed.lineItems || []).map((item: any) => ({
          name: item.name || item.description || '',
          partNumber: item.partNumber || undefined,
          reference: undefined,
          quantity: item.quantity || 1,
          costPrice: item.unitPrice || 0,
          sellPrice: item.unitPrice || 0,
          categoryId: item.category ? categoryIdByName.get(String(item.category).toLowerCase()) : undefined,
        }));
        setParsedItems(items);
        setAttachedSupplier(undefined);
        setParsedSupplierName(parsed.supplierName || '');
        setParsedSupplierEmail(parsed.supplierEmail || '');
        setParsedSupplierPhone(parsed.supplierPhone || '');
        setParsedSupplierAddress(parsed.supplierAddress || '');
        setParsedInvoiceNumber(parsed.invoiceNumber || '');
        if (parsed.invoiceDate) setBulkPurchaseDate(parsed.invoiceDate);
        message.success(`Extracted ${items.length} items from invoice`);
      } catch (error) {
        message.error('Failed to parse invoice. You can add items manually.');
        console.error(error);
      }
      setParsing(false);
    };

    const handleSubmit = async () => {
      if (submitting) return;
      if (parsedItems.length === 0) {
        message.warning('No items to create');
        return;
      }
      if (!bulkSupplierId) {
        message.warning('Supplier is required');
        return;
      }
      if (!parsedInvoiceNumber.trim()) {
        message.warning('Invoice number is required');
        return;
      }
      setSubmitting(true);
      try {
        const id = (await ensureUploaded()) ?? undefined;
        const itemsToSave = parsedItems.map(({ serviceId, ...rest }) => ({
          ...rest,
          purchaseDate: rest.purchaseDate || bulkPurchaseDate || undefined,
        }));
        const created = await InventoryService.createBulk({
          documentId: id,
          supplierId: bulkSupplierId,
          orderId: bulkOrderId,
          supplierInvoiceNumber: parsedInvoiceNumber || undefined,
          items: itemsToSave,
        });
        message.success(`${parsedItems.length} items created`);
        const snapshot = parsedItems;
        reset();
        onSuccess(created ?? [], snapshot);
      } catch (error) {
        message.error('Failed to create items');
        console.error(error);
      } finally {
        setSubmitting(false);
      }
    };

    const handleParsedItemChange = (index: number, field: string, value: any) => {
      setParsedItems((prev) => {
        const updated = [...prev];
        updated[index] = { ...updated[index], [field]: value };
        return updated;
      });
    };

    const handleCreateSupplier = async (values: {
      name: string;
      contactName?: string;
      email?: string;
      phone?: string;
      address?: string;
    }) => {
      setSupplierCreating(true);
      try {
        const supplier = await InventoryService.findOrCreateSupplier(values);
        setAttachedSupplier({ id: supplier.id, name: supplier.name });
        setBulkSupplierId(supplier.id);
        setSupplierModalOpen(false);
        message.success('Supplier saved');
      } catch (error) {
        message.error('Failed to save supplier');
        console.error(error);
      } finally {
        setSupplierCreating(false);
      }
    };

    const handleAttachSupplier = async () => {
      if (!parsedSupplierName) return;
      try {
        const supplier = await InventoryService.findOrCreateSupplier({
          name: parsedSupplierName,
          email: parsedSupplierEmail || undefined,
          phone: parsedSupplierPhone || undefined,
          address: parsedSupplierAddress || undefined,
        });
        setAttachedSupplier({ id: supplier.id, name: supplier.name });
        setBulkSupplierId(supplier.id);
        message.success('Supplier attached');
      } catch (error) {
        message.error('Failed to attach supplier');
        console.error(error);
      }
    };

    return (
      <Presentation
        open={open}
        uploadedFile={uploadedFile}
        parsedItems={parsedItems}
        parsing={parsing}
        captureToken={captureToken}
        parsedSupplierName={parsedSupplierName}
        parsedInvoiceNumber={parsedInvoiceNumber}
        bulkSupplierId={bulkSupplierId}
        attachedSupplier={attachedSupplier}
        categoryOptions={categoryOptions}
        serviceOptions={serviceOptions}
        submitting={submitting}
        supplierModalOpen={supplierModalOpen}
        supplierCreating={supplierCreating}
        onOpenSupplierModal={() => setSupplierModalOpen(true)}
        onCloseSupplierModal={() => setSupplierModalOpen(false)}
        onCreateSupplier={handleCreateSupplier}
        onClose={handleClose}
        onAttachSupplier={handleAttachSupplier}
        onSubmit={handleSubmit}
        onFileSelect={handleFileSelect}
        onRemoveUploadedFile={() => {
          setUploadedFile(null);
          setDocumentId(null);
        }}
        onParse={handleParse}
        onParsedItemChange={handleParsedItemChange}
        onRemoveParsedItem={(i) => setParsedItems((prev) => prev.filter((_, idx) => idx !== i))}
        onAddParsedItem={() =>
          setParsedItems((prev) => [...prev, { name: '', partNumber: '', reference: '', quantity: 1, costPrice: 0, sellPrice: 0 }])
        }
        onBulkSupplierChange={setBulkSupplierId}
        onParsedInvoiceNumberChange={setParsedInvoiceNumber}
        bulkPurchaseDate={bulkPurchaseDate}
        onBulkPurchaseDateChange={setBulkPurchaseDate}
      />
    );
  })
);
